import { FC, useState, useReducer, useEffect, useRef, useMemo } from 'react'
import {
  Box,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Backdrop,
  CircularProgress,
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers'

import { AddPaymentRequest, getPaymentById } from '../../../services'

// Components
import { AppDialog } from '../../../components'

// Redux
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { addPaymentAction, addPaymentResetAction } from '../../../store/items'
import { refreshFetchSlipsAction } from '../../../store/slips'
import { showAlertAction } from '../../../store/alerts'
import { getFormattedDate, replace12AmWithCurrentTime } from '../../../utils'
import CustomNumberField from '../Users/profile/CustomNumberField'
import SiteAutocomplete from '../../../../src/components/SitesAutoComplete/SiteAutocomplete'
import { SiteDto } from '../../../../src/components/SitesAutoComplete/Services'
import { useCommandPaletteActions } from '../../../contexts/CommandPaletteContext'
import { getSlipsLabels } from './slipsLabels'

/** True when editing an existing payment (must load GET payment/pvt before showing form). */
function isEditExistingPayment(it?: AddPaymentRequest | null): it is AddPaymentRequest {
  return typeof it?.paymentId === 'number' && it.paymentId > 0
}

/** Maps GET payment/pvt response (camelCase or snake_case) into AddPaymentRequest. */
function mapPaymentDetailToAddRequest(
  raw: unknown,
  fallback: AddPaymentRequest
): AddPaymentRequest {
  if (raw == null || typeof raw !== 'object') return fallback
  const d = raw as Record<string, unknown>
  const pickStr = (...keys: string[]): string | undefined => {
    for (const k of keys) {
      const v = d[k]
      if (v !== undefined && v !== null && String(v).trim() !== '') return String(v)
    }
    return undefined
  }
  const pickNum = (...keys: string[]): number | undefined => {
    for (const k of keys) {
      const v = d[k]
      if (typeof v === 'number' && !Number.isNaN(v)) return v
      if (typeof v === 'string' && v !== '' && !Number.isNaN(Number(v))) return Number(v)
    }
    return undefined
  }
  const billId = pickNum('bill_id', 'billId', 'site_id', 'siteId') ?? fallback.billId
  const paymentId = pickNum('payment_id', 'paymentId', 'id') ?? fallback.paymentId
  const amount = pickNum('amount') ?? fallback.amount
  const paymentDate =
    pickStr('payment_date', 'paymentDate', 'entry_date', 'entryDate') ?? fallback.paymentDate
  const note = pickStr('note') ?? fallback.note
  const refNo = pickStr('ref_no', 'refNo') ?? fallback.refNo
  const tdsAmount = pickNum('tds_amount', 'tdsAmount') ?? fallback.tdsAmount ?? 0
  const tdsSection = pickStr('tds_section', 'tdsSection') ?? fallback.tdsSection

  return {
    billId,
    paymentId: paymentId != null ? Number(paymentId) : fallback.paymentId,
    amount,
    paymentDate,
    note,
    refNo,
    tdsAmount,
    tdsSection,
  }
}

type IssueItemFields = 'amount' | 'note' | 'refNo'
type IssueItemFieldsState = Record<
  IssueItemFields,
  Partial<{
    value: string
    error: boolean
    helperText: string
  }>
>

type IssueItemErrors = Record<IssueItemFields, Record<string, string>>

const issueItemErrors: IssueItemErrors = {
  amount: {
    empty: 'Please enter amount',
  },
  note: {
    empty: 'Please enter note',
  },

  refNo: {
    empty: 'Please enter refNo',
  },
}

const initialItemIssueState: IssueItemFieldsState = {
  amount: {
    error: false,
    helperText: 'Please enter amount',
  },
  note: {
    error: false,
    helperText: 'Please enter note',
  },
  refNo: {
    error: false,
    helperText: 'Please enter ref no',
  },
}
const itemReducer = (state: Partial<IssueItemFieldsState>, action) => {
  const {
    type,
    payload: { name, value },
    helperText,
  } = action
  switch (type) {
    case 'SET':
      return {
        ...state,
        [name]: {
          ...state[name],
          error: false,
          value: value,
          helperText: helperText
            ? helperText
            : initialItemIssueState[name]?.helperText,
        },
      }
    case 'ERROR':
      return {
        ...state,
        [name]: {
          ...state[name],
          error: true,
          value: value,
          helperText: issueItemErrors[name]['empty'],
        },
      }
    case 'RESET':
      return {
        ...initialItemIssueState,
        [name]: {
          ...state[name],
          error: false,
          value: value,
          helperText: initialItemIssueState[name]?.helperText,
        },
      }
    default:
      return state
  }
}

export interface AddEditPayementProps {
  children?: React.ReactNode
  onClose?: () => void
  onSubmitSuccess?: (site?: SiteDto) => void
  item?: AddPaymentRequest
  accountType?: string
}

export const AddEditPaymentItem: FC<AddEditPayementProps> = ({
  onClose,
  onSubmitSuccess,
  item,
  accountType,
}) => {
  const paymentLabels = getSlipsLabels(accountType)
  const dispatchAction = useAppDispatch()
  const { openNewSite } = useCommandPaletteActions()
  const {
    success: addPaymentSuccess,
    error: addPaymentError,
    message,
  } = useAppSelector((state) => state.addPayment)

  const [fetchedPayment, setFetchedPayment] = useState<AddPaymentRequest | null>(null)
  const [loadingPaymentDetail, setLoadingPaymentDetail] = useState(() => isEditExistingPayment(item))
  const itemRef = useRef(item)
  itemRef.current = item

  const [date, setDate] = useState<Date | null>(() => {
    if (item?.paymentDate != null) return new Date(item.paymentDate)
    return new Date()
  })
  const [isClose, setClosePopup] = useState<boolean>(false)
  const [paymentRequest, setPaymentRequest] = useState<AddPaymentRequest | null>(null)
  const [itemData, dispatch] = useReducer(itemReducer, initialItemIssueState)
  const [request, setRequest] = useState<AddPaymentRequest | null>(null)
  const [site, setSite] = useState<SiteDto | null>(null)

  const paymentTypeInitial = (): 'Received' | 'Refunded' | 'Receive + TDS' => {
    if (!item) return 'Received'
    if (item.amount != null && item.amount < 0) return 'Refunded'
    if (item.tdsAmount != null && item.tdsAmount > 0) return 'Receive + TDS'
    return 'Received'
  }
  const [paymentType, setPaymentType] = useState<'Received' | 'Refunded' | 'Receive + TDS'>(paymentTypeInitial)
  const [tdsAmount, setTdsAmount] = useState<string | number>(
    () => (item?.tdsAmount != null && item.tdsAmount > 0 ? String(item.tdsAmount) : '')
  )
  const lastTdsSectionRef = useRef<string>('')
  const [tdsSection, setTdsSection] = useState<string>(
    () => item?.tdsSection ?? lastTdsSectionRef.current
  )
  // UI-only: gross amount for TDS mode; not sent to API. amount (net) = amountIncludingTDS - tdsAmount
  const [amountIncludingTDS, setAmountIncludingTDS] = useState<string | number>(() => {
    if (item?.tdsAmount != null && item.tdsAmount > 0 && item.amount != null) {
      return item.amount + item.tdsAmount
    }
    return ''
  })

  /** Add (no row) vs add-with-bill vs edit: edit waits for GET payment/pvt. */
  const formItem: AddPaymentRequest | undefined = useMemo(() => {
    if (!item) return undefined
    if (isEditExistingPayment(item)) {
      return fetchedPayment ?? undefined
    }
    return item
  }, [item, fetchedPayment])

  useEffect(() => {
    if (!isEditExistingPayment(item)) {
      setFetchedPayment(null)
      setLoadingPaymentDetail(false)
      return
    }
    setLoadingPaymentDetail(true)
    setFetchedPayment(null)
    let cancelled = false
    const pid = item.paymentId as number
    getPaymentById(pid)
      .then((res) => {
        if (cancelled) return
        const raw = (res.data as { data?: unknown })?.data ?? res.data
        setFetchedPayment(mapPaymentDetailToAddRequest(raw, itemRef.current as AddPaymentRequest))
      })
      .catch(() => {
        if (cancelled) return
        setFetchedPayment(itemRef.current as AddPaymentRequest)
        dispatchAction(
          showAlertAction({
            message: 'Could not load payment details. Showing list data.',
            severity: 'warning',
          })
        )
      })
      .finally(() => {
        if (!cancelled) setLoadingPaymentDetail(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when payment id changes; catch uses itemRef
  }, [item?.paymentId])

  useEffect(() => {
    if (item && isEditExistingPayment(item) && !formItem) {
      return
    }
    if (!formItem) {
      if (!item) {
        setTdsSection(lastTdsSectionRef.current)
      }
      return
    }
    setPaymentRequest(formItem)
    if (formItem.amount != null && formItem.amount < 0) {
      setPaymentType('Refunded')
    } else if (formItem.tdsAmount != null && formItem.tdsAmount > 0) {
      setPaymentType('Receive + TDS')
      setTdsAmount(formItem.tdsAmount)
      setTdsSection(formItem.tdsSection ?? '')
      setAmountIncludingTDS((formItem.amount ?? 0) + (formItem.tdsAmount ?? 0))
    } else {
      setPaymentType('Received')
    }
    if (formItem.paymentDate) {
      setDate(new Date(formItem.paymentDate))
    }
  }, [formItem, item])

  useEffect(() => {
    if (paymentRequest) {
      const displayAmount = paymentRequest.amount
        ? Math.abs(paymentRequest.amount)
        : paymentRequest.amount

      dispatch({
        type: 'SET',
        payload: { name: 'amount', value: displayAmount },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'note', value: paymentRequest.note },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'refNo', value: paymentRequest.refNo },
      })
      setRequest({
        ...request,
        ...(displayAmount != null && { amount: displayAmount }),
        ...(paymentRequest.note != null && { note: paymentRequest.note }),
        ...(paymentRequest.refNo != null && { refNo: paymentRequest.refNo }),
        paymentId: paymentRequest.paymentId,
        billId: paymentRequest.billId,
        paymentDate: getFormattedDate(date),
        ...(paymentRequest.tdsAmount != null && { tdsAmount: paymentRequest.tdsAmount }),
        ...(paymentRequest.tdsSection != null && paymentRequest.tdsSection !== '' && { tdsSection: paymentRequest.tdsSection }),
      })
      if (paymentRequest.tdsAmount != null && paymentRequest.tdsAmount > 0) {
        setTdsAmount(paymentRequest.tdsAmount)
        setAmountIncludingTDS((paymentRequest.amount ?? 0) + (paymentRequest.tdsAmount ?? 0))
      }
      if (paymentRequest.tdsSection != null) {
        setTdsSection(paymentRequest.tdsSection)
      }
    }
  }, [paymentRequest, setRequest, dispatch])

  useEffect(() => {
    if (addPaymentSuccess) {
      dispatchAction(refreshFetchSlipsAction())
      dispatchAction(
        showAlertAction({
          message: 'Item Updated Successfully',
          severity: 'success',
        })
      )
      onSubmitSuccess(site) // To close the popup
    } else if (addPaymentError && message) {
      dispatchAction(showAlertAction({ message: message, severity: 'error' }))
      onSubmitSuccess() // To close the popup
    }
  }, [addPaymentSuccess, addPaymentError, message])

  const onChangeHandler = (e) => {
    const { name, valueAsNumber, value } = e.target
    if (valueAsNumber) {
      dispatch({ type: 'SET', payload: { name, value: valueAsNumber } })
      setRequest({
        ...request,
        [name]: valueAsNumber,
      })
    } else {
      dispatch({ type: 'ERROR', payload: { name, value: value } })
      setRequest({
        ...request,
        [name]: value,
      })
    }
  }

  const dateChangeHandler = (value) => {
    const newDate = replace12AmWithCurrentTime(value)
    setRequest({
      ...request,
      paymentDate: getFormattedDate(newDate),
    })
    setDate(newDate)
  }

  const submitHandle = () => {
    if (loadingPaymentDetail) {
      return
    }
    if (!request) {
      return
    }
    const isReceiveTds = paymentType === 'Receive + TDS'

    if (isReceiveTds) {
      const amtInc = Number(amountIncludingTDS)
      const tds = Number(tdsAmount) || 0
      if (Number.isNaN(amtInc) || amtInc <= 0) {
        dispatchAction(
          showAlertAction({
            message: 'Please enter a valid Amount including TDS',
            severity: 'error',
          })
        )
        return
      }
      const netAmount = Math.round((amtInc - tds) * 100) / 100
      const finalTdsAmount = tds
      const finalTdsSection = tdsSection ?? ''
      if (!item && finalTdsSection) {
        lastTdsSectionRef.current = finalTdsSection
      }
      const finalRequest: AddPaymentRequest = {
        ...request,
        amount: netAmount,
        tdsAmount: finalTdsAmount,
        tdsSection: finalTdsSection,
      }
      dispatchAction(addPaymentAction(finalRequest))
      return
    }

    const checkRequest = Object.keys(request).every((key) => {
      if (key === 'note' || key === 'paymentId' || key === 'refNo' || key === 'tdsAmount' || key === 'tdsRate' || key === 'tdsSection') {
        return true
      }
      return !!request[key]
    })
    if (checkRequest) {
      const finalAmount =
        paymentType === 'Refunded' && request.amount
          ? -Math.abs(Number(request.amount))
          : request.amount
      const finalRequest = {
        ...request,
        amount: finalAmount,
        tdsAmount: 0,
        tdsRate: 0,
        tdsSection: '',
      }
      dispatchAction(addPaymentAction(finalRequest))
    }
  }
  useEffect(() => {
    return () => {
      dispatchAction(addPaymentResetAction()) // To manage the Alert
    }
  }, [])
  return (
    <AppDialog
      id="edit-item"
      title={paymentLabels.paymentDialogTitle}
      submitButtonText="Submit"
      onClose={onClose}
      onSubmit={submitHandle}
      close={isClose}
      maxWidth="sm"
    >
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        spacing={3}
      >
        <Grid item xs={12} sx={{ pb: 2, mx: 2 }} mt={1.5}>
          <Box component="form" noValidate autoComplete="off" sx={{ position: 'relative' }}>
            {loadingPaymentDetail && isEditExistingPayment(item) && (
              <Backdrop
                sx={{
                  position: 'absolute',
                  zIndex: (theme) => theme.zIndex.modal - 1,
                  borderRadius: 1,
                  bgcolor: 'rgba(255,255,255,0.65)',
                }}
                open
              >
                <CircularProgress color="primary" />
              </Backdrop>
            )}
            <Grid container spacing={3}>
              {!item && (
                <Grid item xs={12}>
                  <SiteAutocomplete
                    small
                    onSiteSelected={(site?: SiteDto) => {
                      const addPaymentRequest = {
                        billId: Number(site ? site.id : 0),
                        paymentId: null,
                        amount: null,
                        paymentDate: null,
                        note: null,
                      }
                      setSite(site)
                      setPaymentRequest(addPaymentRequest)
                    }}
                    onAddNewSite={() => {
                      if (openNewSite) openNewSite()
                    }}
                    onCloseDialog={() => {
                      if (onClose) onClose()
                    }}
                  />
                </Grid>
              )}
              {/* Row 2: Type full width; when Receive + TDS, same row Type (50%) + TDS Section (50%) */}
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={paymentType === 'Receive + TDS' ? 6 : 12}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="payment-type-label">Type</InputLabel>
                      <Select
                        labelId="payment-type-label"
                        id="payment-type-select"
                        value={paymentType}
                        label="Type"
                        onChange={(e) => {
                          const newType = e.target.value as 'Received' | 'Refunded' | 'Receive + TDS'
                          setPaymentType(newType)
                          if (newType === 'Receive + TDS') {
                            const amt = Number(itemData.amount.value)
                            if (typeof amt === 'number' && !Number.isNaN(amt) && amt > 0) {
                              setAmountIncludingTDS(amt)
                            }
                          }
                        }}
                      >
                        <MenuItem value="Received">{paymentLabels.paymentTypeReceivedLabel}</MenuItem>
                        <MenuItem value="Refunded">{paymentLabels.paymentTypeRefundedLabel}</MenuItem>
                        <MenuItem value="Receive + TDS">{paymentLabels.paymentTypeReceiveTdsLabel}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {paymentType === 'Receive + TDS' && (
                    <Grid item xs={6}>
                      <TextField
                        size="small"
                        name="tdsSection"
                        value={tdsSection}
                        label="TDS Section (optional)"
                        type="text"
                        onChange={(e) => setTdsSection(e.target.value ?? '')}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              {/* Row 3 (Receive + TDS only): Amount including TDS, TDS amount */}
              {paymentType === 'Receive + TDS' && (
                <Grid item xs={12}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6}>
                      <CustomNumberField
                        maxLength={8}
                        name="amountIncludingTDS"
                        value={amountIncludingTDS}
                        label="Amount including TDS"
                        onChange={(e) => {
                          const v = e.target.valueAsNumber ?? e.target.value
                          const numVal = typeof v === 'number' && !Number.isNaN(v) ? v : (e.target.value ?? '')
                          setAmountIncludingTDS(numVal)
                        }}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CustomNumberField
                        maxLength={8}
                        name="tdsAmount"
                        value={tdsAmount}
                        label="TDS"
                        onChange={(e) => {
                          const v = e.target.valueAsNumber ?? e.target.value
                          const numVal = typeof v === 'number' && !Number.isNaN(v) ? v : (e.target.value ?? '')
                          setTdsAmount(numVal)
                        }}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {/* Row 4: Amount - editable for Received/Refunded, read-only for Receive + TDS */}
              <Grid item xs={12}>
                {paymentType === 'Receive + TDS' ? (
                  <CustomNumberField
                    maxLength={8}
                    name="amount"
                    value={
                      (() => {
                        const amtInc = Number(amountIncludingTDS)
                        const tds = Number(tdsAmount)
                        if (Number.isNaN(amtInc) || Number.isNaN(tds)) return ''
                        return Math.round((amtInc - tds) * 100) / 100
                      })()
                    }
                    label="Amount"
                    fullWidth
                    disabled
                    InputLabelProps={{ shrink: true }}
                    onChange={() => {}}
                  />
                ) : (
                  <CustomNumberField
                    maxLength={8}
                    name="amount"
                    value={itemData.amount.value}
                    label="Amount"
                    onChange={onChangeHandler}
                    fullWidth
                    error={itemData['amount']['error']}
                    helperText={
                      itemData['amount']['error'] ? itemData.amount.helperText : ''
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <DateTimePicker
                  label="Select Date"
                  value={date}
                  ampmInClock={false}
                  onChange={dateChangeHandler}
                  format="dd/MM/yyyy hh:mm a"
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      variant: 'outlined',
                      InputLabelProps: { shrink: true },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  id="id_text_field_refNo"
                  name="refNo"
                  value={itemData.refNo.value ?? ''}
                  label="Cheque no., UTR, transaction no., etc."
                  type="text"
                  onChange={onChangeHandler}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  id="id_text_field_note"
                  multiline
                  rows={4}
                  name="note"
                  value={itemData.note.value ?? ''}
                  label="Note"
                  type="text"
                  onChange={onChangeHandler}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </AppDialog>
  )
}
